import React from 'react';
import { getLeaveTypeBadgeClass } from '../../utils/leaveTypeBadgeColors';
import { formatLeaveDays } from '../../utils/numberUtils';
import { getLeaveTypeAbbreviation } from '../../utils/leaveDisplayUtils';

const CHIP_SIZE_CLASSES = {
  sm: 'px-2 py-0.5 text-[11px]',
  xs: 'px-1.5 py-0.5 text-[10px]',
};

/**
 * Human-readable funding formula, e.g. "10 AL + 16.5 UL".
 * Empty string when there is nothing to show.
 */
export const getConsumptionFormulaTitle = (proposal) => {
  const items = proposal?.items;
  if (!Array.isArray(items) || items.length === 0) return '';

  const visibleItems = items.filter((item) => parseFloat(item.days) > 0);
  if (visibleItems.length === 0) return '';

  const balanceYears = new Set(
    visibleItems
      .map((item) => Number(item.balance_year))
      .filter(Number.isFinite)
  );
  const showBalanceYear = balanceYears.size > 1;

  return visibleItems
    .map((item) => {
      const yearLabel = showBalanceYear && item.balance_year
        ? ` ${item.balance_year}`
        : '';
      return `${formatLeaveDays(parseFloat(item.days))} ${getLeaveTypeAbbreviation(item.leave_type)}${yearLabel}`;
    })
    .join(' + ');
};

const PLUS_SIZE_CLASSES = {
  sm: 'text-[11px]',
  xs: 'text-[10px]',
};

/**
 * Coloured "0.5 OIL + 0.5 AL" chip row from a leave_consumption_proposal.
 * Shared across admin Applications, Balances history, and employee Leave.
 *
 * Chip tokens match LeaveTypeBadge / StatusBadge. Number and abbreviation are
 * separate flex children so they do not kiss.
 *
 * Default wraps at chip boundaries (cards, mobile, detail modals).
 * Table Duration cells pass size="xs" nowrap so the formula stays one line.
 */
const ConsumptionBreakdown = ({
  proposal,
  justify = 'start',
  className,
  size = 'sm',
  nowrap = false,
}) => {
  const items = proposal?.items;
  if (!Array.isArray(items) || items.length === 0) return null;

  const visibleItems = items.filter(item => parseFloat(item.days) > 0);
  if (visibleItems.length === 0) return null;

  const formulaTitle = getConsumptionFormulaTitle(proposal);
  const balanceYears = new Set(
    visibleItems
      .map((item) => Number(item.balance_year))
      .filter(Number.isFinite)
  );
  const showBalanceYear = balanceYears.size > 1;

  const justifyClass =
    justify === 'end' ? 'justify-end' : justify === 'center' ? 'justify-center' : 'justify-start';
  const chipSize = CHIP_SIZE_CLASSES[size] || CHIP_SIZE_CLASSES.sm;
  const plusSize = PLUS_SIZE_CLASSES[size] || PLUS_SIZE_CLASSES.sm;
  const gapClass = size === 'xs' ? 'gap-1' : 'gap-1.5';

  return (
    <div
      className={`inline-flex items-center ${gapClass} ${nowrap ? 'flex-nowrap' : 'flex-wrap'} max-w-full ${justifyClass}${className ? ` ${className}` : ''}`}
      title={formulaTitle}
    >
      {visibleItems.map((item, idx) => {
        const d = parseFloat(item.days);
        const label = getLeaveTypeAbbreviation(item.leave_type);
        const yearLabel = showBalanceYear && item.balance_year
          ? ` ${item.balance_year}`
          : '';
        const colorClasses = getLeaveTypeBadgeClass(item.leave_type);
        return (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <span className={`text-gray-400 ${plusSize} flex-shrink-0`} aria-hidden="true">+</span>
            )}
            <span
              className={`inline-flex items-center gap-1 flex-shrink-0 ring-1 ring-inset ${colorClasses} ${chipSize} rounded-full font-medium whitespace-nowrap`}
            >
              <span className="tabular-nums">{formatLeaveDays(d)}</span>
              <span>{label}{yearLabel}</span>
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * Duration cell: total first, then the funding split on the same line.
 *
 * Approvers scan Duration as a quantity ("how long is this person out?") and
 * only then the split ("what balances / is any of it unpaid?"). Stacking those
 * two facts makes mixed-consumption rows 3× taller than siblings and breaks
 * that scan. A middle-dot separates the facts — never parentheses.
 *
 * `compact` uses LeaveTypeBadge xs tokens and keeps the formula on one line
 * (Applications table). Cards may omit compact so chips can wrap if needed.
 */
export const DurationWithConsumption = ({
  children,
  proposal,
  showBreakdown = false,
  justify = 'end',
  compact = false,
  nowrap,
  className = '',
  title,
}) => {
  const justifyClass = justify === 'end' ? 'justify-end' : 'justify-start';
  const preventWrap = nowrap ?? compact;
  const wrapClass = preventWrap ? 'flex-nowrap' : 'flex-wrap';

  return (
    <div
      className={`inline-flex items-center gap-1 min-w-0 max-w-full ${wrapClass} ${justifyClass}${className ? ` ${className}` : ''}`}
      {...(title ? { title, 'aria-label': title } : {})}
    >
      <span className="whitespace-nowrap flex-shrink-0">{children}</span>
      {showBreakdown && (
        <>
          <span className="text-gray-400 text-xs flex-shrink-0" aria-hidden="true">·</span>
          <ConsumptionBreakdown
            proposal={proposal}
            justify={justify}
            size={compact ? 'xs' : 'sm'}
            nowrap={preventWrap}
          />
        </>
      )}
    </div>
  );
};

export default ConsumptionBreakdown;
